import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList, faLock, faSearch } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { COMPLIANCE_NAV } from './nav';

interface Event {
    id: number; category: string; action: string; actor: string; subject: string;
    description: string; ip_address: string | null; occurred_at: string;
}

const CATEGORY_COLORS: Record<string, string> = {
    phi_access: 'danger', consent: 'info', access_control: 'warning', authentication: 'secondary',
    data_subject: 'primary', breach: 'danger', configuration: 'success', other: 'secondary',
};

const CATEGORIES = ['phi_access', 'consent', 'access_control', 'authentication', 'data_subject', 'breach', 'configuration', 'other'];

const AuditLog: React.FC = () => {
    const { api } = useInternalApi();
    const [events, setEvents] = useState<Event[]>([]);
    const [byCategory, setByCategory] = useState<{ category: string; count: number }[]>([]);
    const [rawFeeds, setRawFeeds] = useState<Record<string, number>>({});
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState('');
    const [search, setSearch] = useState('');

    useEffect(() => { document.title = 'Continuous Audit Log - Uzima Internal'; }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (category) params.set('category', category);
            if (search) params.set('search', search);
            const res = await api.get(`compliance-api/audit-log/?${params}`);
            if (res.data?.status === 'success') {
                setEvents(res.data.data.events || []);
                setByCategory(res.data.data.by_category || []);
                setRawFeeds(res.data.data.raw_feeds || {});
                setNote(res.data.data.note || '');
            }
        } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category]);

    useEffect(() => { load(); }, [load]);

    return (
        <InternalShell activeDashboard="compliance_center" subNav={COMPLIANCE_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faClipboardList} className="me-2 text-primary" />Continuous Audit Log</h2>
            <p className="text-muted mb-4">Append-only record of security and PHI-relevant events</p>

            <div className="alert alert-secondary small"><FontAwesomeIcon icon={faLock} className="me-2" />{note}</div>

            <div className="d-flex gap-2 mb-3">
                <select className="form-select" style={{ maxWidth: 220 }} value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="">All categories</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                </select>
                <div className="input-group" style={{ maxWidth: 340 }}>
                    <span className="input-group-text"><FontAwesomeIcon icon={faSearch} /></span>
                    <input className="form-control" placeholder="Search action, actor, subject…" value={search}
                        onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} />
                    <button className="btn btn-outline-primary" onClick={load}>Search</button>
                </div>
            </div>

            {byCategory.length > 0 && (
                <div className="d-flex gap-2 mb-3 flex-wrap">
                    {byCategory.map(c => (
                        <span key={c.category} className={`badge bg-${CATEGORY_COLORS[c.category] || 'secondary'} p-2`}>{c.category.replace('_', ' ')}: {c.count}</span>
                    ))}
                </div>
            )}

            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : events.length === 0 ? (
                        <div className="text-center py-5 text-muted">No audit events match.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>When</th><th>Category</th><th>Action</th><th>Actor</th><th>Subject</th><th>Detail</th><th>IP</th></tr></thead>
                            <tbody>
                                {events.map(e => (
                                    <tr key={e.id}>
                                        <td className="small">{new Date(e.occurred_at).toLocaleString()}</td>
                                        <td><span className={`badge bg-${CATEGORY_COLORS[e.category] || 'secondary'}`}>{e.category.replace('_', ' ')}</span></td>
                                        <td className="small"><code>{e.action}</code></td>
                                        <td className="small">{e.actor}</td>
                                        <td className="small">{e.subject || '—'}</td>
                                        <td className="small">{e.description}</td>
                                        <td className="small text-muted">{e.ip_address || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-header bg-white"><strong>Pre-existing raw feeds</strong> <span className="text-muted small">— still recording independently; this log is the new consolidated spine, not a retroactive history</span></div>
                <div className="card-body">
                    <div className="d-flex gap-3 flex-wrap">
                        {Object.entries(rawFeeds).map(([k, v]) => (
                            <span key={k} className="badge bg-light text-dark p-2 border">{k.replace(/_/g, ' ')}: {v}</span>
                        ))}
                    </div>
                </div>
            </div>
        </InternalShell>
    );
};

export default AuditLog;
